import { SortType } from '@gp/enums/common';
import { getSpecialListValues, sortByCharsPure, toOptions } from '@gp/utils/common';
import { getNullableMoment, getReqGranularity } from '@gp/utils/date';
import {
  getCascadeProjectDatas,
  getCascadeProjectValues,
  getProjectPriorityName,
  getProjectStageName,
  sortStages,
} from '@gp/utils/project';
import moment from 'moment';
import { CHANGE_CHECKLIST_DATE_TYPES } from '../constants';
import { DEFAULT_FILTER } from '../models/locker';

import type {
  IPipelineChangeChecklistFetchParams,
  IPipelineChangeChecklistFilterConditionsDTO,
  IPipelineChangeChecklistFilterConditionsVO,
  IPipelineChangeChecklistFilterVO,
  IPipelineChangeChecklistRecordVO,
} from '@gp/types/changeLog';
import type { SortBy } from 'tea-component/lib/table/addons';

export const formatFilterConditions = (
  params?: IPipelineChangeChecklistFilterConditionsDTO | null,
): {
  values: IPipelineChangeChecklistFilterVO;
  conditions: IPipelineChangeChecklistFilterConditionsVO;
} => {
  const {
    priority: priorityOptions,
    stage: stageConditions,
    change_check_list_time_range: startEndDate = {},
    pteam_bo_studio_project: pteamProject,
  } = params || {};

  const { pteam_list: pteamList = [] } = pteamProject || {};
  const { start_date: minDate, end_date: maxDate } = startEndDate || {};

  const minDateM = getNullableMoment(minDate);
  const endDateM = getNullableMoment(maxDate) || moment();
  const startDateM = !minDateM
    ? moment(endDateM).subtract('months', 1)
    : moment.max(minDateM, moment(endDateM).subtract('months', 1));

  const priorityConditionsSorted = sortByCharsPure(priorityOptions);
  // const pteamConditionsSorted = sortByCharsPure(pteamConditions);
  const stageConditionsSorted = sortStages(stageConditions);

  const sortedPriorityOptions = toOptions(priorityConditionsSorted, getProjectPriorityName);
  // const sortedPteamOptions = toOptions(pteamConditionsSorted);
  const sortedStageOptions = toOptions(stageConditionsSorted, getProjectStageName);

  // project配置项及初始化全选
  const cascadeProjectoptions = getCascadeProjectDatas(pteamList, 'sensitive_filter_auth');

  return {
    values: {
      rangePicker: {
        ...DEFAULT_FILTER.rangePicker,
        startDate: startDateM.startOf('month').format('YYYY-MM-DD'),
        endDate: endDateM.endOf('month').format('YYYY-MM-DD'),
      },
      priority: priorityConditionsSorted,
      // pteam: pteamConditionsSorted,
      stage: stageConditionsSorted,
      project_id: getCascadeProjectValues(cascadeProjectoptions, true),
    },
    conditions: {
      rangePicker: {
        dateTypes: CHANGE_CHECKLIST_DATE_TYPES,
        minDate: minDate || '',
        maxDate: maxDate || '',
      },
      priority: sortedPriorityOptions,
      // pteam: sortedPteamOptions,
      stage: sortedStageOptions,
      project_id: cascadeProjectoptions || [],
    },
    defaultValues: {
      project_id: getCascadeProjectValues(cascadeProjectoptions, false),
    },
  };
};

export const formatFilterFetchParams = (
  payload?: IPipelineChangeChecklistFilterVO,
  filterConditions?: IPipelineChangeChecklistFilterConditionsVO,
): Omit<
  IPipelineChangeChecklistFetchParams,
  'sort' | 'sort_type' | 'search_type' | 'search' | 'project_ids' | 'page_num' | 'page_size'
> => {
  const { rangePicker, priority, pteam, stage, project_id } = payload || {};

  const {
    priority: priorityOptions,
    pteam: pteamOptions,
    stage: stageOptions,
  } = filterConditions || {};

  const { dateType, startDate, endDate } = rangePicker || {};

  return {
    granularity: getReqGranularity(dateType, 'month'),
    start_date: startDate || '',
    end_date: endDate || '',
    priority: getSpecialListValues(priority, priorityOptions),
    pteam: getSpecialListValues(pteam, pteamOptions),
    stage: getSpecialListValues(stage, stageOptions),
    project_id,
  };
};

export const formatSortFetchParams = (
  sorts?: SortBy<IPipelineChangeChecklistRecordVO>[],
): Pick<IPipelineChangeChecklistFetchParams, 'sort' | 'sort_type'> => {
  const result = {
    sort: '',
    sort_type: SortType.None,
  };
  if (!Array.isArray(sorts) || !sorts.length) return result;
  for (const sort of sorts) {
    const { by, order } = sort || {};
    if (!by || !order) continue;
    result.sort = by;
    result.sort_type = order as SortType;
  }
  return result;
};
